import React, { useState } from 'react';
import { Plus, Eye, Award, Lock, CheckCircle, Clock, XCircle, Banknote, Trash2 } from 'lucide-react';
import Pagination from '../../../common/Pagination';

const PAGE_SIZE = 15;

const fmt = (n) =>
  parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TIPO_LABELS = { LEGAL: 'Legal', FIJA: 'Fija', MIXTA: 'Mixta' };
const TIPO_COLORS = {
  LEGAL: 'bg-blue-50 text-blue-700',
  FIJA:  'bg-violet-50 text-violet-700',
  MIXTA: 'bg-teal-50 text-teal-700',
};

function EstadoBadge({ estado }) {
  const map = {
    borrador: { label: 'Borrador',  cls: 'bg-amber-100 text-amber-700',  Icon: Clock        },
    aprobado: { label: 'Aprobado',  cls: 'bg-blue-100 text-blue-700',    Icon: CheckCircle  },
    pagado:   { label: 'Pagado',    cls: 'bg-green-100 text-green-700',  Icon: Lock         },
    cancelado:{ label: 'Cancelado', cls: 'bg-red-100 text-red-600',      Icon: XCircle      },
  };
  const { label, cls, Icon } = map[estado] || map.borrador;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      <Icon size={10} /> {label}
    </span>
  );
}

export default function ListadoBonificaciones({ lista, onSelect, onCrear, onEliminar, loading }) {
  const [page, setPage] = useState(1);

  const totalPages = Math.ceil(lista.length / PAGE_SIZE);
  const pagedLista = lista.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="space-y-4">

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-gray-900">Bonificaciones Anuales</h2>
          <p className="text-xs text-gray-400 mt-0.5">Participación en beneficios · Código de Trabajo RD</p>
        </div>
        <button
          onClick={onCrear}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={14} />
          Nueva Bonificación
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : lista.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl flex flex-col items-center justify-center py-20">
          <Award size={40} className="text-gray-200 mb-3" />
          <p className="text-sm font-medium text-gray-500">No hay bonificaciones registradas</p>
          <p className="text-xs text-gray-400 mt-1">Crea tu primera bonificación haciendo clic en "Nueva Bonificación".</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Nombre</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Año</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Tipo</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Empleados</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Bonificación</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pagedLista.map(b => (
                <tr key={b.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Banknote size={14} className="text-yellow-500 flex-shrink-0" />
                      <p className="font-medium text-gray-900">{b.nombre}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center font-bold text-gray-900">{b.anio}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${TIPO_COLORS[b.tipo] || ''}`}>
                      {TIPO_LABELS[b.tipo] || b.tipo}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700 font-medium">{b.total_empleados}</td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-900">
                    RD$ {fmt(b.total_bonificacion)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <EstadoBadge estado={b.estado} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {b.estado === 'borrador' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onEliminar(b); }}
                          className="p-1.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                          title="Eliminar borrador"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                      <button
                        onClick={() => onSelect(b)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors"
                      >
                        <Eye size={12} /> Ver
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={page}
            totalPages={totalPages}
            total={lista.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}
